import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import {
  SidebarWrapper,
  SidebarWrapperOverflowContainer,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { selectAccountSlug } from '@/redux/session/selectors';
import type { AccountDebt, DebtStatusEvent } from '@/shared/types/contacts';
import { formatDate } from '@/util/dateUtils';

import { debtInfoFields, FieldGroup, formatMoney, paymentGroupFields, ROLE_KEY } from './DebtsTab';
import {
  DEBT_STATUS_KEY,
  DEBT_STATUS_SOURCE_KEY,
  PAYMENT_EVIDENCE_KEY,
  PAYMENT_METHOD_KEY,
} from '../constants';
import { useDebtEvents } from '../hooks/useDebtEvents';
import { FLAG_KEY, FLAGS } from '../pages/debtColumns';

interface DebtDetailPanelProps {
  debt: AccountDebt;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-sm leading-5 font-medium text-muted-foreground">{children}</h3>
);

const DebtEventRow = ({ event }: { event: DebtStatusEvent }) => {
  const { t } = useTranslation();
  const status = t(DEBT_STATUS_KEY[event.status]);
  const change = event.previous_status
    ? t('contacts.debts.history_changed', {
        0: t(DEBT_STATUS_KEY[event.previous_status]),
        1: status,
      })
    : t('contacts.debts.history_created', { 0: status });
  const details = [
    formatDate(event.created, 'yyyy-MM-dd HH:mm'),
    event.source && t(DEBT_STATUS_SOURCE_KEY[event.source]),
  ].filter(Boolean);
  const payment = [
    event.payment_method && t(PAYMENT_METHOD_KEY[event.payment_method]),
    event.payment_reference,
    event.payment_evidence && t(PAYMENT_EVIDENCE_KEY[event.payment_evidence]),
  ].filter(Boolean);

  return (
    <li className="flex flex-col gap-0.5">
      <span className="text-sm leading-5 text-foreground">{change}</span>
      <span className="text-xs leading-4 text-muted-foreground">{details.join(' · ')}</span>
      {payment.length > 0 && (
        <span className="text-xs leading-4 break-words text-muted-foreground">
          {payment.join(' · ')}
        </span>
      )}
    </li>
  );
};

const DebtHistory = ({ debtId }: { debtId: string }) => {
  const { t } = useTranslation();
  const { canRead, events, status } = useDebtEvents(debtId);

  if (!canRead) {
    return null;
  }

  const renderBody = () => {
    if (status === 'pending') {
      return <Spinner size="sm" />;
    }
    if (status === 'error') {
      return (
        <span className="text-sm leading-5 text-destructive">
          {t('contacts.debts.history_failed')}
        </span>
      );
    }
    if (events.length === 0) {
      return (
        <span className="text-sm leading-5 text-muted-foreground">
          {t('contacts.debts.history_empty')}
        </span>
      );
    }
    return (
      <ul className="flex flex-col gap-3">
        {events.map((event) => (
          <DebtEventRow key={event.event_id} event={event} />
        ))}
      </ul>
    );
  };

  return (
    <>
      <Separator />
      <SectionTitle>{t('contacts.debts.history')}</SectionTitle>
      {renderBody()}
    </>
  );
};

export const DebtDetailPanel = ({ debt }: DebtDetailPanelProps) => {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const onClose = useResponsiveSidebarClose();
  useEscapeKey(onClose);

  const flags = FLAGS.filter((flag) => debt[flag]);
  const balance = formatMoney(debt.current_balance, debt.currency);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle className="pr-4">
        <h2 className="truncate text-base leading-none font-medium text-foreground">
          {debt.debt_external_id || debt.debt_id}
        </h2>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          type="button"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <XIcon size={16} className="text-muted-foreground" />
        </Button>
      </SidebarWrapperTitle>

      <SidebarWrapperOverflowContainer className="gap-4 pb-6">
        <div className="flex items-center justify-between gap-4">
          <span className="text-lg leading-7 font-semibold text-foreground">
            {balance ? `${balance} ${debt.currency}` : t('common.na')}
          </span>
          <Badge type="secondary" flavour="neutral" rounded={false}>
            {t(DEBT_STATUS_KEY[debt.status])}
          </Badge>
        </div>

        <Separator />
        <SectionTitle>{t('contacts.debts.flags')}</SectionTitle>
        {flags.length === 0 ? (
          <span className="text-sm leading-5 text-muted-foreground">
            {t('contacts.debts.no_flags')}
          </span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {flags.map((flag) => (
              <Badge key={flag} type="secondary" flavour="destructive" rounded={false}>
                {t(FLAG_KEY[flag])}
              </Badge>
            ))}
          </div>
        )}

        <Separator />
        <SectionTitle>{t('contacts.debts.parties')}</SectionTitle>
        {debt.parties.length === 0 ? (
          <Badge type="secondary" flavour="warning" rounded={false} className="self-start">
            {t('contacts.debts.no_party')}
          </Badge>
        ) : (
          <ul className="flex flex-col gap-3">
            {debt.parties.map((party) => (
              <li
                key={`${party.contact_id}-${party.role}`}
                className="flex items-baseline justify-between gap-4"
              >
                <Link
                  to={`/${slug}/contacts/${party.contact_id}`}
                  className="min-w-0 truncate text-sm leading-5 text-foreground hover:underline"
                >
                  {party.display_name || t('contacts.unnamed')}
                </Link>
                <span className="shrink-0 text-sm leading-5 text-muted-foreground">
                  {t(ROLE_KEY[party.role])}
                </span>
              </li>
            ))}
          </ul>
        )}

        <Separator />
        <FieldGroup className="px-0" fields={debtInfoFields(debt, t)} />
        <Separator />
        <FieldGroup className="px-0" fields={paymentGroupFields(debt, t)} />
        <DebtHistory debtId={debt.debt_id} />
      </SidebarWrapperOverflowContainer>
    </SidebarWrapper>
  );
};
